import { useEffect, useRef, useState } from 'react';
import { Spinner } from './ui/spinner';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useLocale, useT } from '../i18n/locale-context';
import { formatDeadline } from '../lib/format';
import { nicheHintLine } from '../lib/niche-hint';
import { CheckoutTermsLine } from './checkout-terms-line';
import { nextChargeDate } from '../lib/next-charge';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import { RemoveScroll } from 'react-remove-scroll';
import { prepareCheckoutLink, subscribeOcto } from '../lib/billing-client';
import { useSwipeDownToDismiss } from '../lib/use-swipe-down-to-dismiss';
import { useBackClosesSheet } from '../lib/use-back-closes-sheet';
import { useVisualViewportSheetHeight } from '../lib/use-visual-viewport-sheet-height';
import { useCheckoutSheetPresence } from '../lib/checkout-presence';
import { usePreparedCheckout } from '../lib/use-prepared-checkout';
import { track } from '../lib/funnel';

/**
 * How long a guest's address must stand still before this sheet identifies them
 * in the background and prepares their order. Same value and same reason as the
 * one-time sheet on /plans: `/auth/start` mails a code when the address already
 * has an account, so it must not run per keystroke.
 */
const GUEST_PREPARE_DEBOUNCE_MS = 600;

/**
 * The primary button's DOM id, so /plans can put focus on it — Enter in the
 * guest address field moves to the next control, and on THIS body the next
 * control is «Продолжить на Octobank» (see `useGuestIdentify`'s `onEnter`).
 * Exported rather than spelled twice: a silently renamed id would turn Enter
 * back into a dead key with nothing failing.
 */
export const OCTO_CONTINUE_ID = 'octo-continue';

interface Props {
  open: boolean;
  planKey: string;
  /** subscribe mode: plan name shown as the modal title. */
  planName?: string;
  /** The base subscription price, already formatted (e.g. «399 000 сум»). */
  priceLabel: string;
  /** 'monthly' | 'annual' — decides when the next charge lands. */
  billingPeriod: string;
  metaEventId?: string;
  /** Payme/Octobank method toggle, rendered under the header. Optional — only
   *  passed for OCTO-allowlisted users who can choose between the two gateways. */
  methodTabs?: import('react').ReactNode;
  /** Live count of what the buyer's own niche is worth right now — the value
   *  pill under the price. Same shape/semantics as the Payme modal. Absent → no
   *  pill. */
  nicheHint?: { tier: 'niche' | 'direction' | 'daily'; count: number };
  /** Where the T-7 renewal reminder will go — see CardSaveModal. */
  reminderChannel?: 'telegram' | 'email';
  /**
   * GUEST checkout — the SAME email field the Payme body carries, in the same
   * place, from the SAME hook instance (/plans passes one object to both bodies).
   *
   * Igor, 2026-09-19: «по Octobank почему там нет поля? а если я на первой
   * модалке не ввёл email и перешёл в Octobank — что?» The first cut gated the
   * gateway switch on identification, so tapping Octobank with an empty address
   * left the radio where it was and put an error on a field in the OTHER body —
   * a dead tap. The rule is «точно такое же окно + одно поле email», and it
   * holds for BOTH bodies: the switch is free, and whichever body is on screen
   * asks for the address itself until it has been answered.
   *
   * Because the instance is SHARED, switching Payme ↔ Octobank keeps the
   * address, the revealed code/password stage and the error — and once
   * identified the prompt stops rendering in both bodies at once.
   *
   * ⚠️ Absent for a signed-in buyer, and every branch that reads it is guarded,
   * so the authed sheet is exactly what it was.
   */
  identify?: import('./guest-checkout-identify').GuestIdentify;
  /**
   * `'guest'` — this subscribe belongs to a buyer identified on the sheet
   * itself. Carried to `POST /billing/subscribe-octo` so the hosted creators
   * agree about guests (see `startCheckout`); that endpoint fires no
   * server-side InitiateCheckout today, so nothing is suppressed by it yet.
   */
  via?: 'guest';
  /**
   * «Продолжить на Octobank» was pressed — the gesture that leaves for the
   * provider, on the prepared anchor and on the fallback button alike.
   *
   * The Octobank half of the rule /plans applies to the card sheet: since
   * 2026-09-22 a GUEST's Meta InitiateCheckout waits for a qualified action
   * instead of firing when the sheet opens, because opening one converted 1
   * time in 31 on 19–21.09 against 11 in 86 for members and Meta's CBO campaign
   * optimises on that event. Since 2026-09-23 the MEMBER instance passes this
   * too, for AddPaymentInfo: their IC fired on «Подписаться», so this press is
   * the only gesture that separates paying from looking. /plans dedups by
   * `metaEventId`, which this sheet SHARES with the Payme body — so a buyer who
   * typed a card digit and then switched here sends one of each, not two.
   */
  onProviderHandoff?: () => void;
  onClose: () => void;
}

/**
 * OCTO subscribe confirmation. Unlike the Payme modal, the card is NOT entered
 * here — OCTO is a hosted redirect. So this mirrors the Payme modal's header
 * (plan name + big price), value pill, dated timeline and trust block, then hands
 * off to Octobank's page. The webhook activates + turns auto-renew on when the
 * payment lands.
 */
export function OctoSubscribeModal({
  open,
  planKey,
  planName,
  priceLabel,
  billingPeriod,
  metaEventId,
  methodTabs,
  nicheHint,
  reminderChannel = 'email',
  identify,
  via,
  onProviderHandoff,
  onClose,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Swipe the mobile bottom sheet down to dismiss (same as card-save-modal).
  // `open` is passed so the hook clears its leftover transform on reopen — this
  // modal keeps the component mounted while closed (`if (!open) return null`), so
  // without it a swipe-dismissed sheet stays translated off-screen next open.
  /** The scrolling body — the panel is a flex column with a pinned footer,
   *  so its own scrollTop is always 0. See card-save-modal. */
  const bodyRef = useRef<HTMLDivElement>(null);
  /*
   * The address block stays on THIS open sheet once it has been on it.
   *
   * `identify.active` falls when `commit()` trades the checkout token for a real
   * session. That used to happen after the press; since the prepared pay link
   * (2026-10-01) it happens as soon as the link is ready — while the buyer's
   * finger is on its way to «Продолжить на Octobank». Dropping the focused field
   * there takes the focus with it, and a sheet lifted above the keyboard comes
   * down when its field loses the focus — at a moment the buyer does not choose.
   *
   * ⚠️ Read from the code, not seen in a recording. The lost tap of 2026-10-02
   * (touch on the link, click on the sheet body) happened 160 ms BEFORE this
   * block was removed: it was the tap's own blur dropping the lift, fixed in
   * `use-visual-viewport-sheet-height` (`BLUR_LIFT_GRACE_MS`). This pin removes
   * the other trigger of the same jump.
   *
   * Closing the sheet releases the pin, so a buyer who comes back is not asked a
   * question that has been answered (Igor, 2026-09-21) — on the NEXT open.
   */
  const [identifyPinned, setIdentifyPinned] = useState(false);
  const identifyActive = identify?.active ?? false;
  useEffect(() => {
    if (!open) setIdentifyPinned(false);
    else if (identifyActive) setIdentifyPinned(true);
  }, [open, identifyActive]);
  const swipe = useSwipeDownToDismiss(onClose, open, bodyRef);
  // The phone's Back closes the sheet, not the page (Igor, 10.10).
  useBackClosesSheet(open, () => swipe.dismiss('back'));
  // A checkout sheet is on screen — keep onboarding out. See checkout-presence.
  useCheckoutSheetPresence(open);
  // Keyboard-safe sheet height — see the hook for the full iOS story.
  // `bodyRef`, not the panel: the panel is `overflow-hidden` under the pinned
  // footer, so the hook has to be told which element actually scrolls.
  useVisualViewportSheetHeight(open, bodyRef);

  /*
   * A guest is billable only once `/auth/start` has answered — that token is
   * what `payment_orders.user_id` hangs on. Debounced for the same reason as on
   * /plans: an address that already has an account gets a code mailed to it, so
   * this must follow a pause in typing, never a keystroke.
   */
  const identifyReady = identify?.ready ?? false;
  const identified = identify ? identify.identified : true;
  // The string itself, not just `ready`: see the same effect on /plans — a
  // timer keyed on `ready` alone never restarts after the first well-formed
  // keystroke and identifies the address minus its last letters.
  const identifyEmail = identify?.email ?? '';
  useEffect(() => {
    if (!open || !identify || identified || !identifyReady) return;
    const id = window.setTimeout(() => void identify.ensure(), GUEST_PREPARE_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
    // `identify` is rebuilt every render; only its primitives may be deps.
  }, [open, identified, identifyReady, identifyEmail]);

  /*
   * PREPARE THE LINK WHILE THE BUYER READS THE SHEET.
   *
   * This modal is only on screen when Octobank IS the selected method, so the
   * gateway is settled the moment it opens. Preparing here lets the footer
   * button be a real `<a href>` — see `usePreparedCheckout` for why an anchor is
   * the fix and an async `location.assign` was the bug. Since 2026-10-01 what is
   * prepared is a link to our own API, and the Octobank order is created only
   * when the buyer follows it (`prepareCheckoutLink`).
   */
  const prepared = usePreparedCheckout({
    enabled: open,
    gateway: open ? 'octo' : null,
    identified,
    // A LINK, not an order (2026-10-01): the Octobank order is created when the
    // buyer follows it — see `prepareCheckoutLink`.
    create: () => prepareCheckoutLink('octo', planKey, undefined, metaEventId, undefined, via),
    onReady: (order) => {
      /*
       * GUEST: take the session NOW. `commit()` is async and an anchor
       * navigation cannot be awaited, so the cookie is written while the page is
       * certainly still here rather than racing the click. The link is ready
       * and Octobank's page is one tap away. Never throws; no-op for a member.
       */
      void identify?.commit();
      track('checkout_handoff_shown', {
        checkoutId: metaEventId,
        plan: planKey,
        provider: 'octo',
        orderId: order.orderId,
      });
    },
  });

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setError(null);
      prepared.reset();
    }
    // `prepared.reset` is stable (useCallback with no deps).
  }, [open]);

  // Page scroll behind the modal is locked by the `<RemoveScroll>` wrapper on
  // the return (iOS-correct, matches Radix Sheet) — see card-save-modal.

  if (!open) return null;

  /** Narrowed once — a union member cannot be narrowed inside a JSX callback. */
  const readyOrder = prepared.state.status === 'ready' ? prepared.state : null;

  const nextChargeAt = nextChargeDate(billingPeriod);
  const nextCharge = formatDeadline(nextChargeAt.toISOString(), locale);

  const onContinue = async () => {
    /*
     * GUEST: the account is created HERE, by the button that leaves the site.
     * Octobank is a hosted redirect and the order it comes back to needs a
     * `user_id` (`payment_orders.user_id` is NOT NULL), so identification cannot
     * wait until the return.
     *
     * A `false` is not always a failure — a malformed address, or `code_required`
     * / `password_required` — and in every one of those cases the hook has
     * already put its message and the revealed field inside the block above,
     * which is ON THIS BODY. The buyer answers it and presses the same button
     * again. Resolves true with no request for a signed-in buyer.
     */
    /*
     * GUEST NOT YET IDENTIFIED: identify and stop. The prepared anchor appears
     * once `/auth/start` has answered; finishing this press with
     * `location.assign` from an async callback is the navigation an in-app
     * WebView drops in silence (see `fireOneTime` on /plans). A code/password
     * stage revealed by `ensure()` is re-entered by the next press, as before.
     */
    if (identify && !identify.identified) {
      await identify.ensure();
      return;
    }
    if (identify && !(await identify.ensure())) return;
    /*
     * THE PRESS THAT LEAVES FOR OCTOBANK — a guest's InitiateCheckout (see
     * `onProviderHandoff`). After `ensure()`, because a press that only marks
     * an empty address field has bought nothing; before the request, so the
     * beacon leaves while this page certainly still exists (`location.assign`
     * below ends this document).
     */
    onProviderHandoff?.();
    setLoading(true);
    setError(null);
    try {
      const { redirectUrl } = await subscribeOcto(planKey, metaEventId, via);
      /*
       * GUEST: take the session BEFORE leaving the site.
       *
       * Octobank is a hosted page and the buyer comes back to `/billing/return`,
       * on a browser that must already know who it is — there is no sheet left
       * to adopt on, and `window.location.assign` below ends this document. The
       * order is created and the card is about to be entered, which is as close
       * to "the money has moved" as a redirect flow ever gets before it leaves.
       *
       * Awaited, and before the beacon delay rather than inside it: the cookie
       * has to be written while this page still exists. Never throws — see
       * `GuestIdentify.commit`. No-op for a signed-in buyer.
       */
      await identify?.commit();
      // Yield a macrotask so any analytics beacon leaves before navigation.
      window.setTimeout(() => window.location.assign(redirectUrl), 50);
    } catch {
      setLoading(false);
      setError(t('plans.octo.error'));
    }
  };

  return (
    <RemoveScroll>
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 md:items-center md:p-4"
        role="dialog"
        aria-modal="true"
        onClick={swipe.dismiss}
      >
        <div
          ref={swipe.ref}
          /* `92vh` + the `supports-[height:1dvh]` twin, not a lone `dvh`
             (2026-09-26): `dvh` shipped in Chrome 108, and on Chrome 101 a `dvh`
             inside `max-height` computes to `none` — no cap at all — while
             `--sheet-lift` still applies, which is how a sheet ends up above the
             top of the screen. See the invoice sheet in `routes/plans.tsx`. */
          className="relative mb-[var(--sheet-lift,0px)] flex max-h-[var(--sheet-max,92vh)] supports-[height:1dvh]:max-h-[var(--sheet-max,92dvh)] w-full flex-col overflow-hidden md:mb-0 rounded-t-2xl border border-edge bg-bone-surface shadow-xl outline-none md:max-w-md md:rounded-xl"
          style={swipe.style}
          onClick={(e) => e.stopPropagation()}
          {...swipe.handlers}
        >
          {/* SCROLLING BODY — see card-save-modal for the full rationale.
            `flex-auto` + `min-h-0` (never `flex-1`, whose `flex-basis:0`
            collapses this content-driven panel), and `bodyRef` is handed to the
            swipe hook so drag-to-dismiss still arms only at the real top. */}
          <div
            ref={bodyRef}
            className="relative min-h-0 flex-auto overflow-y-auto overscroll-contain px-5 pb-4 pt-5 md:px-6 md:pt-6"
          >
            {/* Bottom-sheet drag handle (mobile only) — see card-save-modal. */}
            <div className="-mt-1 mb-3 flex justify-center md:hidden">
              <span aria-hidden="true" className="h-1 w-10 rounded-full bg-charcoal/30" />
            </div>
            {/* Same ✕ as the Payme sheet — h-9, full-strength ink, tap area
            unchanged (`before:-inset-2`). See card-save-modal. */}
            <button
              type="button"
              onClick={swipe.dismiss}
              aria-label={t('saved.close')}
              className="absolute right-3 top-0 flex h-9 w-9 items-center justify-center rounded-md text-[15px] text-ink md:top-3 transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone hover:text-ink"
            >
              ✕
            </button>

            {/* HEADER — plan name (with period) as the title, price big + bold below. */}
            <h2 className="text-[16px] font-semibold text-ink">
              {planName
                ? `${planName} ${billingPeriod === 'monthly' ? t('billing.card.periodMonthly') : t('billing.card.periodAnnual')}`.trim()
                : t('plans.octo.title')}
            </h2>
            <div className="mt-1 text-[28px] font-extrabold leading-none tracking-[-0.4px] text-ink">
              {priceLabel}
            </div>

            {/* VALUE PILL — same live count as the Payme modal. Absent → nothing. */}
            {nicheHint && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-dim px-3 py-1.5 text-[12.5px] font-medium leading-snug text-brand">
                <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-brand" />
                <span>{nicheHintLine(nicheHint, t)}</span>
              </div>
            )}

            {/* THE TERMS LINE — same frame, same sentence, same position as on
                the Payme sheet (`CheckoutTermsLine`, 2026-09-23). */}
            {nextCharge && (
              /* `mb-4`: the gateway strip follows directly and its first card sat
                 flush under this line (Igor, iPhone, 2026-09-24). */
              <CheckoutTermsLine className="mb-4" headline={t('billing.card.refund7')}>
                {t('billing.card.renewLine', { date: nextCharge })}{' '}
                {t(
                  reminderChannel === 'telegram'
                    ? 'billing.card.remindTelegram'
                    : 'billing.card.remindEmail',
                )}{' '}
                {t('billing.card.cancelHow')}
              </CheckoutTermsLine>
            )}
            {/* Gateway chooser — before the continue button. */}
            {methodTabs}

            {/* GUEST: the address, BELOW the method strip — the last thing in
            the body, directly above the trust lines and the footer button that
            uses it.

            ⚠️ Same slot as on the Payme body, and that is the whole point
            (Igor, 2026-09-21). There the strip is followed by the address and
            then the card fields; putting the address ABOVE the strip here made
            it jump the length of the strip every time the buyer switched
            gateway — on a sheet whose two bodies are meant to differ by the
            payment method and nothing else. The Payme body has the card below
            this; this one has only the button, so here it is also the bottom.

            ⚠️ `active` only, i.e. until the address has been answered. The
            Payme body keeps the answered address on screen (disabled) because
            it is one field among several on a form the buyer is still filling
            in; this body is a confirmation with a single button, and a dead
            input on it is noise. Null throughout for a signed-in buyer.

            ⚠️ …but never pulled out from under an OPEN sheet (`identifyPinned`,
            2026-10-02): the answered box goes away the next time the sheet
            opens, not at the instant `commit()` lands. */}
            {(identify?.active || identifyPinned) && identify?.node && (
              <div className="mt-4">{identify.node}</div>
            )}

            {/* TRUST BLOCK — mirrors the Payme modal, but names Octobank as the
            processor (the honest gateway for this redirect path). The recurring
            disclosure is the terms line under the price, the same frame and
            sentence as on the Payme sheet (`CheckoutTermsLine`, 2026-09-23). */}
            <div className="mt-4 flex flex-col items-center gap-1.5 text-center text-[12px] leading-snug text-ink-soft">
              <span className="inline-flex items-center justify-center gap-1.5">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 shrink-0 text-brand"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <rect x="5" y="11" width="14" height="9" rx="2" />
                  <path d="M8 11V8a4 4 0 0 1 8 0v3" />
                </svg>
                {t('billing.card.securedByOcto')}
              </span>
              <span>
                <Link
                  to="/offer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {t('billing.card.offerLink')}
                </Link>
                {' · '}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand hover:underline"
                >
                  {t('billing.card.privacyLink')}
                </Link>
                {HAS_TELEGRAM && (
                  <>
                    {' · '}
                    <a
                      href={COMPANY.telegramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-brand hover:underline"
                    >
                      {t('billing.card.supportTg')}
                    </a>
                  </>
                )}
              </span>
            </div>
          </div>

          {/* STICKY CTA FOOTER — identical treatment to card-save-modal: the
            panel's last flex row, so it rides inside `--sheet-max` /
            `--sheet-lift` (never under the iOS keyboard) and the body above it
            is a sibling, not a layer — nothing is hidden underneath and the
            trust block is reached by scrolling to the end. */}
          <div className="shrink-0 border-t border-edge bg-bone-surface px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 md:px-6 md:pb-6">
            {/* Above the button (the slot the Payme modal uses for its SMS helper). */}
            <p className="mb-2 text-center text-[11.5px] leading-snug text-ink-muted">
              {t('plans.octo.redirectNote')}
            </p>

            {/*
              THREE STATES, and only the middle one is ever non-interactive.

              `ready`     — a real `<a href>` to Octobank. Following a link IS the
                            user gesture, and that is the fix: the `assign` below
                            fires from an async handler, outside the tap's gesture
                            window, and mobile engines and in-app WebViews drop it
                            in silence.
              `preparing` — the order is being created so the button can become
                            that link. One round trip, `aria-busy`, and it says
                            what it is doing.
              otherwise   — ⚠️ NEVER disabled for a missing field (Igor,
                            2026-09-19: «кнопка жмётся пусть, но подсвечивает, что
                            не заполнено»). The press runs `ensure()`, whose
                            `precheck` marks the address — or the revealed code /
                            password — and puts the caret in it without reaching
                            any API. A disabled button fires no DOM events and
                            never says why it is dead; that is how 128
                            `checkout_failed{empty_number}` taps stayed invisible
                            for a month. Disabled ONLY while a request is in
                            flight, which is not a validation state.
            */}
            {readyOrder ? (
              <a
                id={OCTO_CONTINUE_ID}
                data-testid="octo-pay-link"
                href={readyOrder.url}
                rel="noopener"
                onClick={() => {
                  // ⚠️ Nothing here may `preventDefault` — following the link IS
                  // the gesture that works (see `usePreparedCheckout`). Both of
                  // these are additive fire-and-forget beacons.
                  onProviderHandoff?.();
                  track('checkout_handoff_link_clicked', {
                    checkoutId: metaEventId,
                    plan: planKey,
                    provider: 'octo',
                    orderId: readyOrder.orderId,
                  });
                }}
                className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid"
              >
                {t('plans.octo.continue')}
              </a>
            ) : prepared.state.status === 'preparing' ? (
              <button
                type="button"
                id={OCTO_CONTINUE_ID}
                data-testid="octo-pay-preparing"
                disabled
                aria-busy="true"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 text-[14px] font-bold text-white disabled:cursor-default disabled:opacity-70"
              >
                <Spinner />
                {t('plans.oncePreparing')}
              </button>
            ) : (
              <button
                type="button"
                id={OCTO_CONTINUE_ID}
                disabled={loading || identify?.busy}
                onClick={() => void onContinue()}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand px-4 text-[14px] font-bold text-white transition-colors hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading || identify?.busy ? <Spinner /> : t('plans.octo.continue')}
              </button>
            )}
            {/* One line when the background preparation failed — the ordinary
                button above is already back, so this explains rather than
                declaring the purchase over. */}
            {prepared.state.status === 'failed' && !error && (
              <p
                className="mt-3 rounded-md border border-status-closing-text/30 bg-status-closing-bg px-3 py-2.5 text-center text-[12.5px] font-medium leading-snug text-status-closing-text"
                role="alert"
              >
                {t('plans.octo.error')}
              </p>
            )}
            {/* Error UNDER the button, as a centered bordered/tinted alert box —
              in the footer with the button, so it cannot scroll out of sight. */}
            {error && (
              <p
                className="mt-3 rounded-md border border-status-closing-text/30 bg-status-closing-bg px-3 py-2.5 text-center text-[12.5px] font-medium leading-snug text-status-closing-text"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </RemoveScroll>
  );
}
